import { useForm } from 'react-hook-form'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useApiClient } from 'hooks/useApiClient'
import { object, date, number } from 'yup'
import { isValid, formatISO } from 'date-fns'
import { Box, Button, InputLabel, TextField } from '@mui/material'
import { yupResolver } from '@hookform/resolvers/yup'
import { useSnackbar } from 'stores/useSnackbar'
import { type Sweepstake } from 'types/api'
import { Modal } from 'components/Modal/Modal'
import { ModalFullBody } from 'components/Modal/ModalFullBody'
import { ModalFullFooter } from 'components/Modal/ModalFullFooter'
import { ModalFullHeader } from 'components/Modal/ModalFullHeader'
import { AdornedDateTimeField } from 'components/AdornedDateTimeField/AdornedDateTimeField'
import { FormFieldErrorMessage } from 'components/FormFieldErrorMessage'
import { useLayout } from 'hooks/useLayout'
import { isPresent } from '@jjvgaming/player-payback-library'

interface LocationSweepstakesConfirmationModalProps {
  isModalOpen: boolean
  toggleIsOpen: () => void
  onConfirm: () => void
  onCancel: () => void
  sweepstake?: Sweepstake
}

export const LocationSweepstakesDrawingSectionAddModal = ({
  isModalOpen,
  toggleIsOpen,
  onConfirm,
  onCancel,
  sweepstake,
}: LocationSweepstakesConfirmationModalProps) => {
  const { isMobile } = useLayout()
  const { post } = useApiClient()
  const queryClient = useQueryClient()
  const setMessage = useSnackbar((state) => state.setMessage)
  const heightModal = isMobile ? '100%' : '40%'
  const title = 'Create New Drawing'

  const SweepstakeDetailsSchema = object().shape({
    Sweepstake: object({
      startDate: date().typeError('Must enter a valid start date'),
      endDate: date()
        .when(['startDate'], ([startDate], schema) => {
          if (isValid(startDate)) {
            return schema.min(
              startDate,
              'End date should not be before start date'
            )
          }
          return schema
        })
        .typeError('Must enter a valid end date'),
      numLocationWinners: number()
        .required('Must enter a number of winners per drawing')
        .transform((val, originalValue) =>
          originalValue === '' ? undefined : val
        )
        .min(1, 'Number of Winners must be greater than or equal to 1')
        .max(99, 'Number of Winners must be less than 100'),
    }),
  })

  const formMethods = useForm({
    resolver: yupResolver(SweepstakeDetailsSchema),
    mode: 'all',
    defaultValues: {
      Sweepstake: {
        startDate: undefined,
        endDate: undefined,
        numLocationWinners: sweepstake?.numLocationWinners ?? 1,
      },
    },
  })

  const {
    register,
    handleSubmit,
    control,
    formState: { errors },
  } = formMethods

  const handleSubmitWrapper = handleSubmit(() => {
    const sDate = formMethods.getValues(`Sweepstake.startDate`)
    const eDate = formMethods.getValues(`Sweepstake.endDate`)
    const numLocationWinners = formMethods.getValues(
      `Sweepstake.numLocationWinners`
    )
    if (sDate && eDate) {
      createSweepstake.mutate({
        startDate: sDate,
        endDate: eDate,
        numLocationWinners,
      })
    }
  })

  const createSweepstake = useMutation({
    mutationKey: ['sweepstakes/create'],
    mutationFn: async (data: {
      startDate: Date
      endDate: Date
      numLocationWinners: number
    }) => {
      const response = await post('/admin/contests', {
        params: { header: undefined },
        body: {
          type: 'LocationSweepstake',
          name: String(sweepstake?.name),
          description: String(sweepstake?.description),
          startDate: formatISO(data.startDate),
          endDate: formatISO(data.endDate),
          groupId: sweepstake?.groupId,
          grandPrizeType: 'None',
          licensedEstablishmentIds:
            Array.isArray(sweepstake?.licensedEstablishmentIds) &&
            sweepstake.licensedEstablishmentIds.length > 0
              ? sweepstake.licensedEstablishmentIds.filter(
                  (id: number | undefined): id is number =>
                    typeof id === 'number'
                )
              : null,
          corporateAccountId: sweepstake?.corporateAccountId,
          organizationId: sweepstake?.organizationId,
          numLocationWinners: data.numLocationWinners,
          numAffidavitDays: sweepstake?.numAffidavitDays,
          marketingSpecialistEmail:
            sweepstake?.marketingSpecialistEmail ?? undefined,
        },
      })
      return response
    },
    onSuccess: async () => {
      setMessage('Sweepstake successfully created', 'success', {
        vertical: 'top',
        horizontal: 'right',
      })
      void queryClient.invalidateQueries({
        queryKey: ['/contests/groupId', sweepstake?.groupId],
      })
      onConfirm()
    },
    onError: async () => {
      setMessage('Error creating sweepstake', 'error', {
        vertical: 'top',
        horizontal: 'right',
      })
    },
  })

  return (
    <Modal
      isOpen={isModalOpen}
      toggleIsOpen={toggleIsOpen}
      sx={{
        width: { xs: '90vw', sm: '40%' },
      }}
    >
      <form onSubmit={handleSubmitWrapper}>
        <ModalFullHeader toggleIsOpen={toggleIsOpen} title={title} />
        <ModalFullBody heightModal={heightModal}>
          <Box display="flex" gap={2} sx={{ maxWidth: '520px' }}>
            <Box flex={1}>
              <AdornedDateTimeField
                name={`Sweepstake.startDate`}
                control={control}
                label="Start Date/Time"
              />
              {errors.Sweepstake?.startDate && (
                <FormFieldErrorMessage
                  message={String(errors.Sweepstake.startDate.message)}
                />
              )}
            </Box>
            <Box flex={1}>
              <AdornedDateTimeField
                name={`Sweepstake.endDate`}
                control={control}
                label="End Date/Time"
              />
              {errors.Sweepstake?.endDate && (
                <FormFieldErrorMessage
                  message={String(errors.Sweepstake.endDate.message)}
                />
              )}
            </Box>
          </Box>
          <Box sx={{ maxWidth: '520px', marginTop: 2 }}>
            <InputLabel
              required
              sx={{ fontWeight: 'normal' }}
              id="Sweepstake.numLocationWinners"
            >
              Number of Winners
            </InputLabel>
            <TextField
              data-testid="NumberOfLocationWinners"
              InputProps={{ inputProps: { min: 1, max: 99 } }}
              fullWidth
              {...register('Sweepstake.numLocationWinners', {
                valueAsNumber: true,
                required: true,
              })}
              type="number"
              placeholder="Enter # of Winners"
              error={isPresent(errors.Sweepstake?.numLocationWinners)}
            />
            {errors.Sweepstake?.numLocationWinners?.message && (
              <FormFieldErrorMessage
                message={errors.Sweepstake.numLocationWinners.message}
              />
            )}
          </Box>
        </ModalFullBody>
        <ModalFullFooter>
          <Button variant="text" onClick={onCancel}>
            Cancel
          </Button>
          <Button
            variant="contained"
            type="submit"
            disabled={createSweepstake.isPending}
          >
            Add
          </Button>
        </ModalFullFooter>
      </form>
    </Modal>
  )
}
